/**
 * Validates an input field for a DisplayName.
 *
 */
import Validator from 'utils/validators/Validator';
import Personalizer from 'utils/Personalizer';
import NumberParser from 'utils/parsers/NumberParser';

var Errors = {
    InvalidNumber: 'invalid_chars',
    NegativeNumber: 'negative_number',
    OutOfRange: 'out_of_range',
    Required: 'required'
};

var NumberValidator = Validator.extend({
    initialize: function(args) {
        this.initializeNumberValidator(args);

        var _this = this;
        this.$inputField.unbind('keydown').bind('keydown', function(e) {
            _this.validCharacterKeydownHandler(e);
        });
    },

    /**
     * Parent class specific initialization function used to get around limitations of inheritance
     * in javascript.
     * @param args
     */
    initializeNumberValidator: function(args) {
        // initialize base class
        this.initializeValidator(args);

        this.validKeys = [
            'ArrowUp',
            'ArrowDown',
            'ArrowLeft',
            'ArrowRight',
            'Tab',
            'Backspace',
            'Delete',
            'End',
            'Home'
        ];

        if (typeof args.typeFormat != 'undefined' && args.typeFormat == 'integer') {
            this.typeFormat = args.typeFormat;
            //tab backspace arrows delete end, home
            this.validChars = [8, 9, 37, 38, 39, 40, 46, 35, 36];
        } else {
            this.typeFormat = 'decimal';
            //tab backspace arrows delete decimal comma period, end, home
            this.validChars = [8, 9, 37, 38, 39, 40, 46, 110, 188, 190, 35, 36];

            this.validKeys.push(',');
            this.validKeys.push('.');
        }

        if (typeof args.mustBePositive != 'undefined') {
            this.mustBePositive = args.mustBePositive; // input setting that restricts the number to a positive number
        } else {
            this.mustBePositive = true;
        }

        if (!args.parser) {
            this.parser = new NumberParser({
                numberSeparator: Personalizer.getNumberSeparator()
            });
        }

        this.conversionFunction = args.conversionFunction; // optional conversion function used for comparing min/max value
        this.minValue = args.minValue; // optional min value for the validator
        this.maxValue = args.maxValue; // optional max value for the validator
        this.required = args.required; // optionally require value

        if (this.mustBePositive == false) {
            this.validChars.push(189); // dash key code - Chrome
            this.validChars.push(173); // dash key code - FF
            this.validChars.push(109); // minus from keypad
        }
    },

    /**
     * Refactored to React as validateInput() in WorkoutPage/utils/validator/NumberValidator.js
     * Any updates to this function after 03-11-2024 also need to be made to the new React function validateInput()
     * This function will be deleted once the workout page has been completely finished in React
     */
    validateInput: function(value) {
        if (this.required && (!value || value.length < 1)) {
            return Errors.Required;
        }

        if (value.length > 0) {
            // Use the parser to validate the number
            try {
                value = this.parser.parse(value);
            } catch (err) {
                return Errors.InvalidNumber;
            }

            if (typeof value !== 'undefined' && !isNaN(value)) {
                var numberValue = +value;

                if (this.conversionFunction) {
                    numberValue = this.conversionFunction.call(this, numberValue);
                }

                if (typeof this.minValue != 'undefined' && numberValue < this.minValue) {
                    return Errors.OutOfRange;
                }

                if (typeof this.maxValue != 'undefined' && numberValue > this.maxValue) {
                    return Errors.OutOfRange;
                }

                // Might be better served to replace this with minValue = 0. We get better error reporting this way though
                if (this.mustBePositive) {
                    if (numberValue < 0) {
                        return Errors.NegativeNumber;
                    }
                }
            } else {
                return Errors.InvalidNumber;
            }
        }

        return;
    },

    /**
     * This is a handler that can be wired up to a keydown event by derived validators. The validChars
     * array will need to be defined by that object as well.
     * @param e
     * @returns {boolean}
     */
    validCharacterKeydownHandler: function(e) {
        var oe = e.originalEvent;
        var ch = typeof oe.which === 'number' ? oe.which : oe.keyCode;

        var allow = false;

        /*
         * Character detection using which and keyCode has been removed from the w3 specs,
         * however, the new methods, code and key, are not widely supported yet(~70% global share on 5/10/17)
         * Try to utilize the new method, but fall back to the old if not yet supported.
         * The new method will prevent the $#% being counted as number characters
         */
        if (oe.key && typeof oe.key === 'string') {
            allow = this.isNumberKey(oe.key);
        } else if (!(oe.key && typeof oe.key === 'string') && this.isNumberKeyCode(ch)) {
            allow = true;
        }

        if (this.validChars.indexOf(ch) > -1) {
            allow = true;
        }

        if (!allow) {
            e.preventDefault();
            return false;
        }
    },

    isNumberKeyCode: function(ch) {
        return !this.isNotANumberKeyCode(ch);
    },

    isNumberKey: function(key) {
        return /\d/.test(key) || this.validKeys.indexOf(key) > -1; // check that key is a digit, or is in the validKeys array
    },

    isNotANumberKeyCode: function(ch) {
        return ch < 48 || (ch > 57 && ch < 96) || ch > 105;
    },

    setMaxValue: function(maxValue) {
        this.maxValue = maxValue;
    },

    setMinValue: function(minValue) {
        this.minValue = minValue;
    },

    setRequired: function(required) {
        this.required = required;
    }
});

NumberValidator.Errors = Errors;

export default NumberValidator;
